/**
 * ChecklistTile — things to tick (a packing list, steps, a shopping list).
 *
 * data: { items: { text: string; done: boolean }[] }
 *
 * Ticking, adding and removing write straight back through the tile's
 * `onChange` (the pane's OCC PATCH), so the list is the person's to work
 * through, not a picture of one. Without `onChange` (an expanded preview)
 * it renders read-only.
 */
import * as React from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/cn";
import type { TileBodyProps } from "../registry";

interface Item {
  text: string;
  done?: boolean;
}

const MAX_ITEMS = 100; // components.MAX_ITEMS

export function ChecklistTile({ data, onChange }: TileBodyProps) {
  const items = ((data as { items?: Item[] }).items ?? []).map((i) => ({
    text: i.text,
    done: Boolean(i.done),
  }));
  const [draft, setDraft] = React.useState("");
  const done = items.filter((i) => i.done).length;

  const write = (next: Item[]) => onChange?.({ ...data, items: next });

  return (
    <div className="flex h-full flex-col gap-2 text-[13px]">
      {items.length > 0 && (
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-faint">
          <span>
            {done}/{items.length} done
          </span>
          <span className="h-1 flex-1 bg-bg-sunken">
            <span
              className="block h-full bg-success"
              style={{ width: `${(done / items.length) * 100}%` }}
            />
          </span>
        </div>
      )}
      <ul className="min-h-0 flex-1 space-y-0.5 overflow-auto">
        {items.map((item, i) => (
          <li key={i} className="group/item flex items-start gap-2 px-1 py-0.5 hover:bg-bg-sunken/60">
            <input
              type="checkbox"
              checked={item.done}
              disabled={!onChange}
              aria-label={item.text}
              onChange={() =>
                write(items.map((it, j) => (j === i ? { ...it, done: !it.done } : it)))
              }
              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-accent"
            />
            <span className={cn("flex-1", item.done ? "text-fg-faint line-through" : "text-fg")}>
              {item.text}
            </span>
            {onChange && (
              <button
                type="button"
                aria-label={`Remove ${item.text}`}
                onClick={() => write(items.filter((_, j) => j !== i))}
                className="text-fg-faint opacity-0 transition hover:text-danger group-hover/item:opacity-100"
              >
                <X size={11} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {onChange && items.length < MAX_ITEMS && (
        <form
          className="flex flex-shrink-0 items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            write([...items, { text, done: false }]);
            setDraft("");
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add an item"
            maxLength={300}
            className="min-w-0 flex-1 border border-border bg-bg px-2 py-1 text-[12.5px] text-fg outline-none focus:border-accent"
          />
          <button
            type="submit"
            aria-label="Add item"
            className="flex h-7 w-7 items-center justify-center bg-fg text-bg transition hover:bg-accent hover:text-accent-fg"
          >
            <Plus size={13} />
          </button>
        </form>
      )}
    </div>
  );
}
